import { V1_EXIT_IDS } from '../../config/v1/exits.js';
import { V1_PRODUCT_IDS } from '../../config/v1/products.js';
import { getVisibleV1TriageQuestions } from '../../config/v1/triage.js';
import { resolveV1CreditBand } from '../creditScoreInput.js';

const num = (v) => {
  const n = parseFloat(String(v || '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const downPaymentPct = (answers) => {
  const price = num(answers.purchasePrice);
  const down = num(answers.downPayment);
  if (price <= 0) return 0;
  return (down / price) * 100;
};

/** Early exits after any triage answer */
export function checkV1TriageExit(answers) {
  if (answers.primaryResidence === 'no') return V1_EXIT_IDS.INVESTMENT_SECOND_HOME;
  if (answers.citizenship === 'other') return V1_EXIT_IDS.NON_CITIZEN;
  if (answers.incomeType === 'other') {
    return answers.goal === 'refinance' ? V1_EXIT_IDS.NON_W2_REFI : V1_EXIT_IDS.SELF_EMPLOYED;
  }
  if (answers.propertyType === 'other') return V1_EXIT_IDS.PROPERTY_TYPE;
  if (resolveV1CreditBand(answers) === 'below-580') return V1_EXIT_IDS.CREDIT_BELOW_580;

  if (answers.goal === 'purchase') {
    const pct = downPaymentPct(answers);
    if (answers.purchasePrice && answers.downPayment && pct <= 0) {
      return V1_EXIT_IDS.ZERO_DOWN;
    }
    if (answers.purchasePrice && answers.downPayment) {
      const credit = resolveV1CreditBand(answers);
      if (credit === '580-619' && pct < 3.5) return V1_EXIT_IDS.INSUFFICIENT_DOWN;
      if (credit && credit !== 'below-580' && credit !== '580-619' && pct < 3) {
        return V1_EXIT_IDS.INSUFFICIENT_DOWN;
      }
    }
  }

  if (answers.goal === 'refinance') {
    if (answers.currentLoanType === 'other') return V1_EXIT_IDS.OTHER_LOAN_TYPE;
    if (answers.currentLoanType === 'fha' && answers.refiGoal === 'cash-out') {
      return V1_EXIT_IDS.FHA_CASH_OUT;
    }
  }

  return null;
}

/** Route when all visible triage questions are answered */
export function routeV1TriageComplete(answers) {
  const exitId = checkV1TriageExit(answers);
  if (exitId) return { type: 'exit', exitId };

  if (answers.goal === 'purchase') {
    const pct = downPaymentPct(answers);
    const credit = resolveV1CreditBand(answers);
    const creditOkForFha = credit && credit !== 'below-580';
    const creditOkForConv = credit === '620-679' || credit === '680-739' || credit === '740-plus';
    const fitsFha = creditOkForFha && pct >= 3.5;
    const fitsConv = creditOkForConv && pct >= 3;

    if (fitsFha && fitsConv) {
      return {
        type: 'choose',
        productIds: [V1_PRODUCT_IDS.FHA_PURCHASE, V1_PRODUCT_IDS.CONVENTIONAL_PURCHASE],
      };
    }
    if (fitsFha) return { type: 'product', productId: V1_PRODUCT_IDS.FHA_PURCHASE };
    if (fitsConv) return { type: 'product', productId: V1_PRODUCT_IDS.CONVENTIONAL_PURCHASE };

    return { type: 'exit', exitId: V1_EXIT_IDS.INSUFFICIENT_DOWN };
  }

  if (answers.goal === 'refinance') {
    if (answers.currentLoanType === 'fha' && answers.refiGoal === 'lower-payment') {
      return { type: 'product', productId: V1_PRODUCT_IDS.FHA_STREAMLINE };
    }
    if (answers.currentLoanType === 'va') {
      return { type: 'product', productId: V1_PRODUCT_IDS.VA_IRRRL };
    }
    if (answers.currentLoanType === 'conventional') {
      if (answers.refiGoal === 'lower-payment') {
        return { type: 'product', productId: V1_PRODUCT_IDS.CONVENTIONAL_RATE_TERM };
      }
      if (answers.refiGoal === 'cash-out') {
        return { type: 'product', productId: V1_PRODUCT_IDS.CONVENTIONAL_CASH_OUT };
      }
    }
  }

  return { type: 'exit', exitId: V1_EXIT_IDS.OTHER_LOAN_TYPE };
}

export function isV1TriageComplete(answers) {
  const visible = getVisibleV1TriageQuestions(answers);
  return visible.every((q) => {
    const v = answers[q.id];
    return v !== undefined && v !== null && String(v).trim() !== '';
  });
}
